import type { MedicationRequest, Resource } from '@medplum/fhirtypes'
import {
  DOSESPOT_DRUG_SYSTEM,
  DOSESPOT_MEDICATION_STATUS_EXT,
  DOSESPOT_PRESCRIBER_ID_EXT,
  DOSESPOT_STATUS_EXT,
} from '@/model/middleware-types'

/**
 * How the chart tells a DoseSpot-synced prescription from anything else.
 *
 * The test is the **transmission-status extension**, not the identifier: the
 * prescription identifier is clinic-scoped (`https://dosespot.com/clinic/{id}/prescription`)
 * and the browser does not know the clinic id. The write-back sync always writes this
 * extension — defaulting to DoseSpot's own `Unknown` when it has to — precisely so a
 * synced row can never be invisible here.
 */
export function isDoseSpotPrescription(resource: Resource): resource is MedicationRequest {
  return (
    resource.resourceType === 'MedicationRequest' &&
    resource.extension?.some((ext) => ext.url === DOSESPOT_STATUS_EXT) === true
  )
}

function extensionValue(prescription: MedicationRequest, url: string): string | undefined {
  return prescription.extension?.find((ext) => ext.url === url)?.valueString
}

/**
 * The raw status pill — **DoseSpot's own vocabulary, both axes, verbatim**
 * (`"eRxSent · Active"`).
 *
 * Deliberately not the FHIR `status`: FHIR cannot say `Error`, and a transmission error
 * is exactly what a prescriber needs to see. The derived `status` still exists on the
 * resource for everything that queries FHIR; this is what the human reads.
 */
export function doseSpotStatusPill(prescription: MedicationRequest): string {
  const transmission = extensionValue(prescription, DOSESPOT_STATUS_EXT)
  const clinical = extensionValue(prescription, DOSESPOT_MEDICATION_STATUS_EXT)
  const axes = [transmission, clinical].filter(Boolean)
  // Only reachable on a row whose extension carries no value at all, which the sync
  // never writes — but the pill is what makes the row legible, so it never renders empty.
  return axes.length > 0 ? axes.join(' · ') : prescription.status || 'unknown'
}

/**
 * The drug as DoseSpot named it — `DisplayName` + `Strength`, which the sync joins into
 * `text`. Returns `undefined` rather than a placeholder so each surface supplies its own
 * fallback.
 */
export function doseSpotMedicationText(prescription: MedicationRequest): string | undefined {
  const concept = prescription.medicationCodeableConcept
  return concept?.text || concept?.coding?.[0]?.display || concept?.coding?.[0]?.code || undefined
}

/**
 * What the pharmacy is being asked to hand over: `Qty 30 · 2 refills · 30 day supply`.
 *
 * Every part is optional because DoseSpot sends them independently, and none is inferred
 * from another — a missing refill count is not zero refills.
 */
export function doseSpotDispenseLabel(prescription: MedicationRequest): string {
  const dispense = prescription.dispenseRequest
  const refills = dispense?.numberOfRepeatsAllowed

  return [
    dispense?.quantity?.value !== undefined ? `Qty ${dispense.quantity.value}` : undefined,
    refills !== undefined ? `${refills} refill${refills === 1 ? '' : 's'}` : undefined,
    dispense?.expectedSupplyDuration?.value !== undefined
      ? `${dispense.expectedSupplyDuration.value} day supply`
      : undefined,
  ]
    .filter(Boolean)
    .join(' · ')
}

// In the order the sync writes them, so the first match is the best code the row has.
const DRUG_CODE_LABELS: Record<string, string> = {
  'http://www.nlm.nih.gov/research/umls/rxnorm': 'RxNorm',
  'http://hl7.org/fhir/sid/ndc': 'NDC',
  [DOSESPOT_DRUG_SYSTEM]: 'DoseSpot drug id',
}

/**
 * How well the drug is coded — `RxNorm`, `NDC`, or DoseSpot's own dispensable-drug id.
 *
 * A prescription carries no RxNorm and no NDC of its own; those come from the sync's
 * cached drug lookup, so a row that missed the lookup falls back to the DoseSpot id.
 * **Absent means the row is genuinely uncoded** — a `FreeText` or `Compound` prescription,
 * which is text-only by construction — and that is worth seeing on the chart.
 */
export function doseSpotDrugCodeLabel(prescription: MedicationRequest): string | undefined {
  for (const coding of prescription.medicationCodeableConcept?.coding ?? []) {
    const label = coding.system ? DRUG_CODE_LABELS[coding.system] : undefined
    if (label) return label
  }
  return undefined
}

/**
 * Did the prescription fail to transmit? `Error`/`EpcsError` mean the pharmacy never got
 * it — the one thing the raw pill exists to make visible, since FHIR's `status` vocabulary
 * cannot express it and the derived status lands on `unknown`.
 */
export function isDoseSpotTransmissionError(prescription: MedicationRequest): boolean {
  const transmission = extensionValue(prescription, DOSESPOT_STATUS_EXT)?.toLowerCase()
  return transmission === 'error' || transmission === 'epcserror'
}

/**
 * Who prescribed it, per §11.6: the prescription's own `PrescriberId` is authoritative,
 * never the clinician BeHuman SSO'd — DoseSpot's certified app offers "On Behalf of" and
 * BeHuman cannot suppress it.
 *
 * **`requester` absent alongside the prescriber-id extension is the unmapped flag** —
 * there is no separate boolean — so this keys on `requester` presence, not on whether a
 * name resolved. A mapped prescriber whose name did not resolve leaves the cell empty,
 * matching how the note-authored rows render an unresolved `informationSource`.
 */
export function doseSpotPrescriberLabel(prescription: MedicationRequest): string {
  if (prescription.requester) return prescription.requester.display || ''

  const prescriberId = extensionValue(prescription, DOSESPOT_PRESCRIBER_ID_EXT)
  return prescriberId ? `External prescriber (DoseSpot #${prescriberId})` : 'External prescriber'
}
